import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getCustomers } from '../api';
import api from '../api';

const REGION_COLORS = {
  'us-west-2':      '#378ADD',
  'eu-west-2':      '#1D9E75',
  'eu-west-3':      '#D85A30',
  'ap-southeast-2': '#7F77DD',
};

const REGION_LABELS = {
  'us-west-2':      'USW2',
  'eu-west-2':      'EUW2',
  'eu-west-3':      'EUW3',
  'ap-southeast-2': 'ASE2',
};

export default function Volumes({ dimension, activeOnly }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected]   = useState('');
  const [data, setData]           = useState(null);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  useEffect(() => {
    getCustomers(activeOnly).then(r => {
      setCustomers(r.data);
      setSelected('');
      setData(null);
    });
    api.get('/api/v1/volumes/summary')
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [activeOnly]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/api/v1/volumes/${selected}?dimension=${dimension}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selected, dimension]);

  // All regions seen across snapshots
  const regions = data
    ? [...new Set(data.snapshots.flatMap(s => s.by_region.map(r => r.region)))]
    : [];

  // Chart data — one bar per region per snapshot
  const chartData = (data?.snapshots || []).map(s => {
    const entry = {
      date: new Date(s.snapshot_date).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      total: s.total_gb,
    };
    s.by_region.forEach(r => { entry[r.region] = r.size_gb; });
    return entry;
  });

  const latest = data?.snapshots?.[data.snapshots.length - 1];

  return (
    <>
      {/* Summary metrics */}
      {summary && (
        <div className="metrics" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
          <div className="metric">
            <div className="metric-label">Total allocated (all customers)</div>
            <div className="metric-value">{summary.total_gb.toLocaleString()} GB</div>
            <div className="metric-sub">{summary.snapshot_date}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Total E: volumes</div>
            <div className="metric-value">{summary.total_volumes.toLocaleString()}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Customers with volumes</div>
            <div className="metric-value">{summary.customer_count}</div>
          </div>
          <div className="metric">
            <div className="metric-label">Largest customer</div>
            <div className="metric-value" style={{ fontSize: 15 }}>
              {summary.customers?.[0]?.cust_name || '—'}
            </div>
            <div className="metric-sub">{summary.customers?.[0]?.total_gb} GB</div>
          </div>
        </div>
      )}

      {/* Customer selector */}
      <div style={{ marginBottom: 16 }}>
        <select
          style={{ fontSize: 13, padding: '7px 12px', border: '1px solid #e5e5e5', borderRadius: 6, minWidth: 280 }}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">Select a customer...</option>
          {[...customers].sort((a, b) => a.cust_name.localeCompare(b.cust_name)).map(c => (
            <option key={c.cust_code} value={c.cust_code}>{c.cust_name}</option>
          ))}
        </select>
      </div>

      {!selected && <div className="loading">Select a customer to view volume allocation</div>}
      {loading && <div className="loading">Loading...</div>}
      {error   && <div className="error">Error: {error}</div>}

      {data && !loading && (
        <>
          {/* Metrics */}
          <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
            <div className="metric">
              <div className="metric-label">Current allocated</div>
              <div className="metric-value">{(data.latest_total_gb || 0).toLocaleString()} GB</div>
              <div className="metric-sub">{latest?.snapshot_date}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Regions</div>
              <div className="metric-value">{regions.length}</div>
              <div className="metric-sub">{regions.map(r => REGION_LABELS[r] || r).join(', ')}</div>
            </div>
            <div className="metric">
              <div className="metric-label">Volumes (E: drive)</div>
              <div className="metric-value">{latest?.volumes?.length || 0}</div>
            </div>
          </div>

          {/* Bar chart — allocated GB by region over time */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                {data.customer?.cust_name} — allocated storage over time (GB)
              </div>
              <div className="legend">
                {regions.map(r => (
                  <span key={r}>
                    <span className="legend-dot" style={{ background: REGION_COLORS[r] || '#888' }}></span>
                    {REGION_LABELS[r] || r}
                  </span>
                ))}
              </div>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}GB`} />
                  <Tooltip formatter={(v, name) => [`${v} GB`, REGION_LABELS[name] || name]} />
                  {regions.map((r, i) => (
                    <Bar
                      key={r}
                      dataKey={r}
                      stackId="a"
                      fill={REGION_COLORS[r] || '#888'}
                      radius={i === regions.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Latest snapshot breakdown by region */}
          {latest && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">By region — {latest.snapshot_date}</div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Allocated (GB)</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.by_region
                    .sort((a, b) => b.size_gb - a.size_gb)
                    .map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{REGION_LABELS[r.region] || r.region}</td>
                      <td>{r.size_gb.toLocaleString()} GB</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e5e5e5' }}>
                    <td style={{ fontWeight: 500 }}>Total</td>
                    <td style={{ fontWeight: 500 }}>{latest.total_gb.toLocaleString()} GB</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Individual volumes table */}
          {latest && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Individual volumes — {latest.snapshot_date}</div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Region</th>
                    <th>Size (GB)</th>
                    <th>State</th>
                    <th>Volume ID</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.volumes
                    .sort((a, b) => b.size_gb - a.size_gb)
                    .map((v, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{v.volume_name || '—'}</td>
                      <td style={{ color: '#666' }}>{REGION_LABELS[v.region] || v.region}</td>
                      <td>{v.size_gb.toLocaleString()} GB</td>
                      <td>
                        <span className={`badge ${v.state === 'in-use' ? 'badge-active' : 'badge-inactive'}`}>
                          {v.state}
                        </span>
                      </td>
                      <td style={{ color: '#999', fontSize: 11 }}>{v.volume_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
